import { A11yModule } from '@angular/cdk/a11y';
import { Component, DestroyRef, HostListener, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Category, CategoryService, Organization, OrganizationService } from 'riterz-core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { AppService } from '../../services/app.service';

@Component({
  selector: 'rt-search',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    RouterModule,
    AvatarModule,
    A11yModule
  ],
  standalone: true,
  templateUrl: 'search.component.html',
  styleUrl: 'search.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class SearchComponent {
  private appService = inject(AppService);
  private router = inject(Router);
  private organizationService = inject(OrganizationService);
  private categoryService = inject(CategoryService);
  private destroyRef = inject(DestroyRef);

  readonly searchForm = new FormGroup({
    term: new FormControl('', Validators.required),
  });

  readonly show = signal<boolean>(true);
  readonly empty = signal<boolean>(true);
  readonly suggestions = signal<string[]>([]);
  readonly organizations = signal<Organization[]>([]);
  readonly categories = signal<Category[]>([]);

  constructor() {
    this.appService.showSearch$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(show => {
        this.show.set(show);
        if (!show) {
          this.searchForm.reset();
          this.empty.set(true);
        }
      });

    this.searchForm.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.empty.set(!value.term);
        this.getOrganizations();
        this.getCategories();
      });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.hide();
    }
  }

  protected getOrganizations(): void {
    if (!this.searchForm.valid) {
      this.organizations.set([]);
      return;
    }

    const params = {
      term: this.searchForm.value.term ?? '',
      per_page: 5
    }
    this.organizationService
      .getPublicOrganizations(params)
      .subscribe((response) => {
        this.organizations.set(response.data);
      });
  }

  protected getCategories(): void {
    if (!this.searchForm.valid) {
      this.categories.set([]);
      return;
    }

    const params = {
      name: this.searchForm.value.term ?? '',
      per_page: 5
    }
    this.categoryService
      .public
      .getCategories(params)
      .subscribe((response) => {
        this.categories.set(response.data);
      });
  }

  protected selectOrganization(organization: Organization): void {
    this.router.navigate(['/profile', organization.slug]);
    this.hide();
  }

  protected selectCategory(category: Category): void {
    const queryParams: any = {};
    queryParams[category.section] = category.slug;

    this.router.navigate(['/search'], { queryParams });
    this.hide();
  }

  protected hide(): void {
    this.appService.hideSearch();
  }

}